import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useUpdatePassword } from "../services";
import { isApiError } from "@/shared/api";

export default function PasswordSetting() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { mutate: updatePassword, isPending } = useUpdatePassword({
    onSuccess: (data) => {
      toast.success(data.message);
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // Redirect to sign-in page after a short delay
      setTimeout(() => {
        navigate("/sign-in", {
          state: { message: "Please log in with your new password." },
        });
      }, 1500);
    },
    onError: (error: unknown) => {
      let message = "Failed to update password. Please try again.";
      let requestId: string | undefined;

      if (isApiError(error)) {
        message = error.message;
        requestId = error.requestId;
      } else if (error instanceof Error) {
        message = error.message;
      }

      toast.error(message, {
        description: requestId ? `Request ID: ${requestId}` : undefined,
      });
    },
  });

  const rules = useMemo(() => {
    const hasMinLength = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(newPassword);
    const passwordsMatch =
      newPassword.length > 0 && newPassword === confirmPassword;
    return {
      hasMinLength,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      passwordsMatch,
    };
  }, [newPassword, confirmPassword]);

  const isFormValid = useMemo(() => {
    return (
      currentPassword.length > 0 &&
      rules.hasMinLength &&
      rules.hasUppercase &&
      rules.hasLowercase &&
      rules.hasNumber &&
      rules.hasSpecialChar &&
      rules.passwordsMatch
    );
  }, [currentPassword, rules]);

  const containerClass =
    "mt-3 border border-red-600/50 bg-red-600/10 rounded-xl p-4 text-sm";
  const itemClass = (ok: boolean) => (ok ? "text-green-400" : "text-red-400");

  function onResetPassword() {
    if (!isFormValid) {
      toast.error("Please fill in all fields and meet password requirements.");
      return;
    }

    updatePassword({
      currentPassword,
      newPassword,
      confirmPassword,
    });
  }

  return (
    <div className="w-full h-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg sm:text-xl font-medium">Password Settings</h2>
      </div>

      {/* Change Password Section */}
      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="text-base font-medium">Change Password</h3>
          <p className="text-xs text-white/60">
            Please enter your current password and set a new one.
          </p>
        </div>

        {/* Current Password */}
        <div className="space-y-2">
          <Label>Current Password</Label>
          <div className="relative">
            <Input
              type={showCurrent ? "text" : "password"}
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isPending}
            />
            <button
              type="button"
              aria-label={showCurrent ? "Hide password" : "Show password"}
              onClick={() => setShowCurrent((v) => !v)}
              disabled={isPending}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white disabled:opacity-50">
              {showCurrent ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        </div>

        {/* New Passwords */}
        <div className="space-y-2">
          <p className="text-sm text-white">Set a New Password</p>
          <div className="space-y-2">
            <Label>New Password</Label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                placeholder="Enter a new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isPending}
              />
              <button
                type="button"
                aria-label={showNew ? "Hide password" : "Show password"}
                onClick={() => setShowNew((v) => !v)}
                disabled={isPending}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white disabled:opacity-50">
                {showNew ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Retype New Password</Label>
            <div className="relative">
              <Input
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter the new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isPending}
              />
              <button
                type="button"
                aria-label={showConfirm ? "Hide password" : "Show password"}
                onClick={() => setShowConfirm((v) => !v)}
                disabled={isPending}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white disabled:opacity-50">
                {showConfirm ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
          </div>

          {/* Password rules */}
          <div className={containerClass}>
            <p className={itemClass(rules.hasMinLength)}>
              {rules.hasMinLength ? "✓" : "✕"} Has at least 8 Characters
            </p>
            <p className={itemClass(rules.hasUppercase)}>
              {rules.hasUppercase ? "✓" : "✕"} Has uppercase letters
            </p>
            <p className={itemClass(rules.hasLowercase)}>
              {rules.hasLowercase ? "✓" : "✕"} Has lowercase letters
            </p>
            <p className={itemClass(rules.hasNumber)}>
              {rules.hasNumber ? "✓" : "✕"} Has numbers
            </p>
            <p className={itemClass(rules.hasSpecialChar)}>
              {rules.hasSpecialChar ? "✓" : "✕"} Has non-alphanumeric characters
            </p>
            <p className={itemClass(rules.passwordsMatch)}>
              {rules.passwordsMatch ? "✓" : "✕"} New passwords match
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="text-sm underline text-white">
            Forgot Password?
          </Link>
          <Button
            onClick={onResetPassword}
            className="min-w-[160px]"
            variant="primary"
            disabled={!isFormValid || isPending}>
            {isPending ? "Updating..." : "Reset Password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
